import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import RichTextEditor, { PREVIEW_BREAK_RE } from "../components/RichTextEditor";
import CoverImageDropzone from "../components/CoverImageDropzone";
import useDraft from "../lib/useDraft";
import TocPanel from "../components/editor/TocPanel";
import GlossaryPanel from "../components/GlossaryPanel";
import { DraftBanner, DraftStatus } from "../components/editor/DraftNotice";
import { useInternalLinks } from "../lib/internalLinks";
import { ARTICLE_CATEGORIES } from "../lib/articleCategories";
import { ArrowLeft, Eye, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { confirmDialog } from "../components/ConfirmDialog";
import { AuthorPicker } from "../components/editor/EditorFields";

const TIERS = [
  { id: "free", label: "Gratuit — visible pour tous" },
  { id: "essentiel", label: "Essentiel — 9,99€/mois" },
  { id: "pro", label: "Pro — 19,99€/mois" },
  { id: "max", label: "Max — 29,99€/mois" },
];

const EMPTY = {
  title: "",
  slug: "",
  category: "crypto",
  excerpt: "",
  content: "",
  cover_image: "",
  tier_required: "free",
  preview_char_limit: 500,
  published: true,
  tags: [],
  toc: null,
  glossary_off: [], // mots du glossaire à ne pas souligner dans cette analyse (ids)
  author_ids: [], // vide = l'admin connecté
};

function fromArticle(a) {
  return {
    title: a.title || "",
    slug: a.slug || "",
    category: a.category || "analyse",
    excerpt: a.excerpt_auto ? "" : a.excerpt || "", // chapô vide : on ne reprend pas le début du texte
    content: a.content || "",
    cover_image: a.cover_image || "",
    tier_required: a.tier_required || "free",
    preview_char_limit: a.preview_char_limit || 500,
    published: a.published !== false,
    tags: a.tags || [],
    toc: a.toc || null,
    glossary_off: a.glossary_off || [],
    author_ids: a.author_ids || [],
  };
}

export default function AdminArticleEditorPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const loadedId = useRef(null);

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["admin-articles"],
    queryFn: async () => (await api.get("/admin/articles")).data,
  });

  const article = isEdit ? articles.find((a) => a.id === id) : null;
  // Définitions du glossaire : bouton « Glossaire » de l'éditeur et mots détectés automatiquement (comme dans les cours)
  const { data: allCourses = [] } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const glossaryTerms = useMemo(
    () => allCourses.filter((c) => c.category === "vocabulary" && c.published !== false),
    [allCourses]
  );
  const slugForView = article?.slug || form.slug;

  // Load article into form once (edit mode)
  useEffect(() => {
    if (isEdit && article && loadedId.current !== id) {
      loadedId.current = id;
      setForm(fromArticle(article));
      setDirty(false);
    }
  }, [isEdit, article, id]);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  // Brouillon automatique (sur cet appareil) et liens internes
  const draft = useDraft(`draft:article:${id || "new"}`, form, { ready: !isEdit || (!!article && loadedId.current === id), dirty });
  const { targets: linkTargets } = useInternalLinks();
  const internalTargets = useMemo(() => linkTargets.filter((t) => t.key !== `article:${id}`), [linkTargets, id]);

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/articles", payload)).data,
    onSuccess: (created) => {
      qc.setQueryData(["admin-articles"], (old = []) => [created, ...old]);
      qc.invalidateQueries({ queryKey: ["admin-articles"] });
      setDirty(false);
      draft.clear();
      loadedId.current = created.id;
      toast.success("Article créé");
      navigate("/admin/articles");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const updateMut = useMutation({
    mutationFn: async (payload) => (await api.patch(`/articles/${id}`, payload)).data,
    onSuccess: (updated) => {
      qc.setQueryData(["admin-articles"], (old = []) =>
        old.map((a) => (a.id === id ? updated : a)),
      );
      qc.invalidateQueries({ queryKey: ["admin-articles"] });
      setDirty(false);
      draft.clear();
      toast.success("Article mis à jour");
      navigate("/admin/articles");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = useCallback(() => {
    if (saving) return;
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Titre et contenu requis");
      return;
    }
    const payload = { ...form, author_ids: form.author_ids?.length ? form.author_ids : undefined };
    if (isEdit) updateMut.mutate(payload);
    else createMut.mutate(payload);
  }, [saving, form, isEdit, updateMut, createMut]);

  // Ctrl/Cmd + S
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  // Warn before unload if dirty
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const goBack = async () => {
    if (dirty && !(await confirmDialog({ title: "Quitter sans enregistrer ?", description: "Vos dernières modifications ne sont pas enregistrées. Le brouillon reste sur cet appareil.", confirmLabel: "Quitter", tone: "warning" }))) return;
    navigate("/admin/articles");
  };

  const viewArticle = () => {
    if (slugForView) window.open(`/articles/${slugForView}`, "_blank");
  };

  const stripTags = (html) => (html || "").replace(/<[^>]*>/g, "");
  const hasBreak = (form.content || "").includes("data-preview-break");
  const totalChars = stripTags(form.content).length;
  const visibleChars = hasBreak
    ? stripTags((form.content || "").split(PREVIEW_BREAK_RE)[0]).length
    : Math.min(form.preview_char_limit || 0, totalChars);
  const previewPct = totalChars > 0 ? Math.min(100, Math.round((visibleChars / totalChars) * 100)) : 0;
  const removeBreak = () => update({ content: (form.content || "").replace(PREVIEW_BREAK_RE, "") });

  if (isEdit && isLoading && !article) {
    return <div className="mx-auto max-w-7xl px-6 py-10 text-zinc-500">Chargement…</div>;
  }
  if (isEdit && !isLoading && !article) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-10">
        <p className="text-zinc-500">Article introuvable.</p>
        <Button variant="outline" className="mt-4 border-white/10" onClick={() => navigate("/admin/articles")}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Analyses
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-10" data-testid="admin-article-editor">
      <DraftBanner draft={draft} onRestore={(d) => { setForm(d); setDirty(true); toast.success("Brouillon récupéré"); }} />
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack} className="text-zinc-400 hover:text-white" data-testid="editor-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Analyses
          </Button>
          <h1 className="font-display font-black text-2xl">
            {isEdit ? "Modifier l'article" : "Nouvel article"}
          </h1>
        </div>
        <div className="text-xs" data-testid="editor-dirty-indicator">
          {dirty ? (
            <span className="inline-flex items-center gap-1.5 text-orange-400">
              <AlertCircle className="w-4 h-4" /> Modifications non enregistrées
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-emerald-400">
              <CheckCircle2 className="w-4 h-4" /> Tout est enregistré
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start lg:min-h-[calc(100vh-6rem)]">
        {/* Colonne gauche — rédaction */}
        <div className="min-w-0 space-y-6">
          <div className="border-b border-white/10 pb-3">
            <input
              data-testid="article-form-title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Titre de l'analyse…"
              className="w-full bg-transparent border-0 outline-none font-display font-black text-4xl leading-tight placeholder:text-zinc-700"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Chapô</Label>
            <Textarea
              data-testid="article-form-excerpt"
              value={form.excerpt}
              onChange={(e) => update({ excerpt: e.target.value })}
              rows={2}
              placeholder="Résumé affiché dans les listes et aperçus."
              className="bg-white/[0.03] border-white/10"
            />
          </div>

          <div className="grid gap-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Contenu</Label>
            <RichTextEditor
              value={form.content}
              onChange={(html) => update({ content: html })}
              placeholder="## Introduction..."
              stickyTop="4rem"
              minHeight="60vh"
              enablePreviewBreak={form.tier_required !== "free"}
              accent="violet"
              glossaryTerms={glossaryTerms}
              glossaryOff={form.glossary_off}
              internalTargets={internalTargets}
              leads={[{ label: "le titre", text: form.title }, { label: "le chapô", text: form.excerpt }]}
              onNormalize={(html) => setForm((prev) => ({ ...prev, content: html }))}
            />
          </div>
        </div>

        {/* Colonne droite — réglages */}
        <div className="space-y-5 lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-1" data-testid="editor-sidebar">
          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Publication</div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="article-form-published"
                checked={form.published}
                onCheckedChange={(v) => update({ published: v })}
              />
              <Label className="cursor-pointer text-sm">{form.published ? "Publié" : "Brouillon"}</Label>
            </div>
            <AuthorPicker
              value={form.author_ids || []}
              onChange={(ids) => update({ author_ids: ids })}
              currentUserId={user?.id}
              testid="article-form-author"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={save}
                disabled={saving}
                className="flex-1 bg-emerald-500 text-black hover:bg-emerald-300"
                data-testid="article-form-submit"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-white/10 shrink-0"
                onClick={viewArticle}
                disabled={!slugForView}
                title="Voir l'article"
                data-testid="editor-view-article"
              >
                <Eye className="w-4 h-4" />
              </Button>
            </div>
            <DraftStatus draft={draft} />
          </Card>
          <Card className="p-5 bg-card border-white/5 space-y-3" data-testid="article-form-toc">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Sommaire</div>
            <TocPanel html={form.content} toc={form.toc} onChange={(toc) => update({ toc })} />
          </Card>

          <GlossaryPanel
            html={form.content}
            terms={glossaryTerms}
            off={form.glossary_off}
            onChange={(off) => update({ glossary_off: off })}
            testid="article-glossary-panel"
          />

          <Card className="p-5 bg-card border-white/5 space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Image de couverture</div>
            <CoverImageDropzone
              value={form.cover_image}
              onChange={(url) => update({ cover_image: url })}
              testid="article-form-cover"
            />
          </Card>

          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Classement</div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                <SelectTrigger data-testid="article-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ARTICLE_CATEGORIES.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Slug (optionnel)</Label>
              <Input
                data-testid="article-form-slug"
                value={form.slug}
                onChange={(e) => update({ slug: e.target.value })}
                placeholder="auto depuis le titre"
                className="bg-white/[0.03] border-white/10"
              />
            </div>
          </Card>

          <Card className="p-5 bg-card border-white/5 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">Accès & aperçu</div>
            <div className="grid gap-2">
              <Label className="text-xs text-zinc-500">Palier requis</Label>
              <Select value={form.tier_required} onValueChange={(v) => update({ tier_required: v })}>
                <SelectTrigger data-testid="article-form-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIERS.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.tier_required === "free" ? (
              <p className="text-xs text-zinc-400 leading-relaxed" data-testid="preview-free-note">
                Article gratuit : il est visible en entier par tout le monde.
              </p>
            ) : (
              <>
                {hasBreak ? (
                  <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 p-3 space-y-2.5" data-testid="preview-break-status">
                    <p className="text-xs text-violet-200 leading-relaxed">
                      ✓ Coupure placée dans le texte — les non-abonnés voient tout ce qui est au-dessus du trait violet.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-violet-500/40 text-violet-200 hover:bg-violet-500/20"
                      onClick={removeBreak}
                      data-testid="remove-preview-break"
                    >
                      Retirer la coupure
                    </Button>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      Placez le curseur dans le texte à l'endroit voulu, puis cliquez sur « ✂ Fin d'aperçu » dans la barre d'outils.
                    </p>
                    <div className="grid gap-2">
                      <Label className="text-xs text-zinc-500">Limite d'aperçu (caractères visibles pour les non-abonnés) — solution de secours</Label>
                      <Input
                        data-testid="article-form-preview-limit"
                        type="number"
                        min={50}
                        max={5000}
                        value={form.preview_char_limit}
                        onChange={(e) => update({ preview_char_limit: Number(e.target.value) || 500 })}
                        className="bg-white/[0.03] border-white/10"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-1.5" data-testid="preview-progress">
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-violet-500 transition-all" style={{ width: `${previewPct}%` }} />
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    Visible gratuitement : {visibleChars} / {totalChars} car. · {previewPct} %
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
